import React from 'react';
import SoaDisclaimerPicker from './SoaDisclaimerPicker';
import { SoaDisclaimerMode, SoaDisclaimerStatus } from '@/types/soa';
import { Button } from '@/components/ui';
import styles from './SoaSendModal.module.css';

interface SoaSendModalProps {
    selectedCount: number;
    unassignedCount: number;
    alreadySentCount: number;
    /** Assigned but short of ALL_VERIFIED — nothing can be sent while this is > 0. */
    unverifiedCount: number;
    /** True outside production: everything is redirected and no client is contacted. */
    isTestMode: boolean;
    /** Full Report (quarterly) vs 2 Pages + Disclaimer, for the whole batch. */
    quarterly: boolean;
    onQuarterlyChange: (value: boolean) => void;
    /** Monthly statements append it, so a monthly send is refused without a valid one. */
    disclaimerStatus: SoaDisclaimerStatus | null;
    onDisclaimerStatusChange: (status: SoaDisclaimerStatus) => void;
    disclaimerMode: SoaDisclaimerMode;
    onDisclaimerModeChange: (mode: SoaDisclaimerMode) => void;
    disclaimerText: string;
    onDisclaimerTextChange: (text: string) => void;
    subject: string;
    onSubjectChange: (value: string) => void;
    body: string;
    onBodyChange: (value: string) => void;
    availableVariables: string[];
    recipients: string;
    onRecipientsChange: (value: string) => void;
    force: boolean;
    onForceChange: (value: boolean) => void;
    sending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const SoaSendModal: React.FC<SoaSendModalProps> = ({
    selectedCount,
    unassignedCount,
    alreadySentCount,
    unverifiedCount,
    isTestMode,
    quarterly,
    onQuarterlyChange,
    disclaimerStatus,
    onDisclaimerStatusChange,
    disclaimerMode,
    onDisclaimerModeChange,
    disclaimerText,
    onDisclaimerTextChange,
    subject,
    onSubjectChange,
    body,
    onBodyChange,
    availableVariables,
    recipients,
    onRecipientsChange,
    force,
    onForceChange,
    sending,
    onCancel,
    onConfirm,
}) => (
    <div className={styles.overlay} onClick={sending ? undefined : onCancel}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.header}>
                <h3>Send SOA Emails</h3>
                <p>
                    You are about to email <strong>{selectedCount}</strong> statement
                    {selectedCount === 1 ? '' : 's'}.
                    {unassignedCount > 0 && ` ${unassignedCount} unassigned will be skipped.`}
                </p>
            </div>

            <div className={styles.body}>
                {unverifiedCount > 0 && (
                    <div className={`${styles.banner} ${styles.bannerError}`} style={{ marginBottom: 16 }}>
                        <span>
                            <strong>
                                {unverifiedCount} selected statement{unverifiedCount === 1 ? '' : 's'}{' '}
                                {unverifiedCount === 1 ? 'is' : 'are'} not fully verified.
                            </strong>{' '}
                            The Verified column names which check failed — PAN, FA Account, or Email.
                            Fix or reassign them before sending; nothing in this batch will be sent
                            until then.
                        </span>
                    </div>
                )}

                <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        Report Type<span className={styles.required}>*</span>
                    </h4>
                    <div className={styles.typeOptions}>
                        <label className={`${styles.typeCard} ${!quarterly ? styles.typeCardSelected : ''}`}>
                            <span className={styles.typeCardHead}>
                                <input
                                    type="radio"
                                    name="soa-report-type"
                                    checked={!quarterly}
                                    onChange={() => onQuarterlyChange(false)}
                                />
                                Monthly
                            </span>
                            <span className={styles.typeCardSub}>2 pages + disclaimer</span>
                        </label>
                        <label className={`${styles.typeCard} ${quarterly ? styles.typeCardSelected : ''}`}>
                            <span className={styles.typeCardHead}>
                                <input
                                    type="radio"
                                    name="soa-report-type"
                                    checked={quarterly}
                                    onChange={() => onQuarterlyChange(true)}
                                />
                                Quarterly
                            </span>
                            <span className={styles.typeCardSub}>Full report, no disclaimer</span>
                        </label>
                    </div>
                    <div className={styles.hint}>
                        Applies to every statement in this batch, regardless of its own month.
                    </div>

                    {!quarterly && (
                        <div style={{ marginTop: 14 }}>
                            <SoaDisclaimerPicker
                                mode={disclaimerMode}
                                onModeChange={onDisclaimerModeChange}
                                text={disclaimerText}
                                onTextChange={onDisclaimerTextChange}
                                disclaimerStatus={disclaimerStatus}
                                onDisclaimerStatusChange={onDisclaimerStatusChange}
                                disabled={sending}
                            />
                            <div className={styles.hint}>
                                Appended to every statement in this batch.
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Email Content</h4>

                    <div className={`${styles.banner} ${styles.bannerInfo}`} style={{ marginBottom: 14 }}>
                        <span>
                            Insert
                            {availableVariables.map(name => (
                                <code key={name} className={styles.variableChip}>{`{{${name}}}`}</code>
                            ))}
                            in the subject or body — each is replaced with that client&apos;s own value.
                            Saved as the template for future sends too.
                        </span>
                    </div>

                    <div className={styles.field}>
                        <label>Subject</label>
                        <input
                            className={styles.input}
                            type="text"
                            value={subject}
                            onChange={e => onSubjectChange(e.target.value)}
                            disabled={sending}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Body (HTML)</label>
                        <textarea
                            className={styles.input}
                            style={{ fontFamily: 'var(--font-mono)', resize: 'vertical' }}
                            rows={7}
                            value={body}
                            onChange={e => onBodyChange(e.target.value)}
                            disabled={sending}
                        />
                    </div>
                </div>

                <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Recipients</h4>

                    {isTestMode ? (
                        <div className={styles.field}>
                            <label>
                                Send To (test)<span className={styles.required}>*</span>
                            </label>
                            <input
                                className={styles.input}
                                type="text"
                                value={recipients}
                                onChange={e => onRecipientsChange(e.target.value)}
                                placeholder="you@sameeksha.capital"
                            />
                            <div className={styles.hint}>
                                Test mode — every statement goes to this address instead of the client,
                                the distributor and RM are not copied, and nothing is marked as emailed.
                                Required: an address here is what keeps this run away from real clients.
                            </div>
                        </div>
                    ) : (
                        <div className={styles.hint} style={{ marginTop: 0 }}>
                            Statements go to each client&apos;s own email address, with their
                            distributor and RM on cc.
                        </div>
                    )}

                    {alreadySentCount > 0 && (
                        <div style={{ marginTop: 14 }}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={force}
                                    onChange={e => onForceChange(e.target.checked)}
                                />
                                Re-send the {alreadySentCount} already emailed
                            </label>
                            <div className={styles.hint}>
                                Leave unticked to skip them and send only the rest.
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.footer}>
                <Button onClick={onCancel} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={onConfirm}
                    // A blank test address would fall through to real clients if this
                    // build is pointed at a production API, so require one here.
                    disabled={
                        sending || unverifiedCount > 0 || (isTestMode && !recipients.trim())
                        || (!quarterly && disclaimerMode === 'pdf' && !disclaimerStatus?.exists)
                        || (!quarterly && disclaimerMode === 'text' && !disclaimerText.trim())
                    }
                >
                    {sending ? 'Sending…' : 'Send Emails'}
                </Button>
            </div>
        </div>
    </div>
);

export default SoaSendModal;
